import { useTheme } from "styled-components";
import {
  Check as CheckIcon,
  CircleDashed,
  Clock3,
  LucideIcon,
  X,
} from "lucide-react";
import Pill from "common/NewLook/Pill.tsx";
import { Tone } from "common/NewLook/IconTile.tsx";
import { Check, CheckCancelIcon, Close, Ellipsis } from "common/Icons/Icons";
import { hexToRGB } from "@shared/utils/misc";
import { OrderStatus } from "@shared/types";
import Tag from "common/Tags/Tag.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import { CSSProperties, useMemo } from "react";

interface OrderCardStatusProps {
  status: OrderStatus;
}

const OrderCardStatus = ({ status }: OrderCardStatusProps) => {
  const theme = useTheme();

  const statusToColor = {
    [OrderStatus.FULFILLED]: theme.colors.darkGreen,
    [OrderStatus.PARTIALLY_FULFILLED]: theme.colors.mediumGrey,
    [OrderStatus.PENDING]: theme.colors.darkYellow,
    [OrderStatus.CANCELLED]: theme.colors.darkRed,
  };

  const statusToBgColor = {
    [OrderStatus.FULFILLED]: theme.colors.green,
    [OrderStatus.PARTIALLY_FULFILLED]: theme.colors.secondaryGrey,
    [OrderStatus.PENDING]: theme.colors.yellow,
    [OrderStatus.CANCELLED]: theme.colors.red,
  };

  const iconStyle: CSSProperties = {
    fontSize: typography.fontSizes.S,
    marginRight: 6,
    top: -1,
    position: "relative",
  };

  const statusToIcon = {
    [OrderStatus.FULFILLED]: <Check style={iconStyle} />,
    [OrderStatus.PARTIALLY_FULFILLED]: (
      <CheckCancelIcon style={{ ...iconStyle, width: 13 }} />
    ),
    [OrderStatus.PENDING]: (
      <Ellipsis style={{ ...iconStyle, top: 0, marginRight: 4 }} />
    ),
    [OrderStatus.CANCELLED]: (
      <Close style={{ ...iconStyle, fontSize: typography.fontSizes.XS }} />
    ),
  };

  const statusTagData = useMemo(() => {
    const orderStatus = Object.values(OrderStatus).find((orderStatus) =>
      status.toLowerCase().startsWith(orderStatus.toLowerCase())
    );
    if (orderStatus) {
      return {
        color: statusToColor?.[orderStatus],
        bgColor: statusToBgColor?.[orderStatus],
        icon: statusToIcon?.[orderStatus],
      };
    }
  }, [status]);

  if (theme.isNewLook) {
    const orderStatus = Object.values(OrderStatus).find((orderStatus) =>
      status.toLowerCase().startsWith(orderStatus.toLowerCase())
    );
    const { tone, icon } =
      (orderStatus && NEW_LOOK_STATUS[orderStatus]) ??
      NEW_LOOK_STATUS[OrderStatus.PENDING];
    return (
      <Pill tone={tone} icon={icon}>
        {status}
      </Pill>
    );
  }

  return (
    <Tag
      bgColor={hexToRGB(statusTagData?.bgColor, 0.12)}
      color={statusTagData?.color || ""}
      style={{
        display: "flex",
        alignItems: "center",
        fontSize: typography.fontSizes.S,
        whiteSpace: "nowrap",
      }}
    >
      {statusTagData?.icon}
      {status}
    </Tag>
  );
};

export default OrderCardStatus;

const NEW_LOOK_STATUS: Record<OrderStatus, { tone: Tone; icon: LucideIcon }> = {
  [OrderStatus.FULFILLED]: { tone: "green", icon: CheckIcon },
  [OrderStatus.PARTIALLY_FULFILLED]: { tone: "blue", icon: CircleDashed },
  [OrderStatus.PENDING]: { tone: "amber", icon: Clock3 },
  [OrderStatus.CANCELLED]: { tone: "grey", icon: X },
};
